/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useReducer } from "react";
import { useOutletContext, useLocation, useSearchParams } from "react-router-dom";
import { Pencil, Plus, Search, Trash, SearchX, ChevronLeft, ChevronRight } from "lucide-react";
import { isAxiosError } from "axios";
import { axiosErrorToStringError } from "@/utils/axiosErrorToStringError";
import { ScaleLoader } from "react-spinners";
import { Link } from "@/router";
import { useAdminGetRoles, useAdminDeleteRole, useAdminGetRolesDefinitionsSWR, useAdminGetRightsSWR } from "@/services/roleService";
import { useAdminGetSkillsSWR } from "@/services/skillService";
import { useAdminGetCompetitionsSWR } from "@/services/competitionService";
import { objectToQueryString } from "@/utils/objectToQueryString";
import { queryStringToObject } from "@/utils/queryStringToObject";


const initialState = {
    errorMessage: "",
    isLoading: false,
    filters: {
        id: "",
        name: "",
        right: "",
        type: "",
        skill_id: "",
        competition_id: "",
        hidden: "",
    },
    roles_definitions: null,
    roles: null,
    page_size: 10,
    page: 1,
    total: 0
};


const ACTIONS = {
    SET_ERROR_MESSAGE: "setErrorMessage",
    SET_IS_LOADING: "setIsLoading",
    SET_FILTER: "setFilter",
    SET_FILTERS: "setFilters",
    SET_ROLES_DEFINITIONS: "setRolesDefinitions",
    SET_ROLES: "setRoles",
    SET_PAGE_SIZE: "setPageSize",
    SET_PAGE: "setPage",
    SET_TOTAL: "setTotal",
}


const reducer = (state, action) => {
    switch (action.type) {
        case "setErrorMessage":
            return { ...state, errorMessage: action.payload };
        case "setIsLoading":
            return { ...state, isLoading: action.payload };
        case "setFilter":
            return { ...state, filters: { ...state.filters, [action.subtype]: action.payload } };
        case "setFilters":
            return { ...state, filters: { ...state.filters, ...action.payload } };
        case "setRolesDefinitions":
            return { ...state, roles_definitions: action.payload };
        case "setRoles":
            return { ...state, roles: action.payload };
        case "setPageSize":
            return { ...state, page_size: action.payload }
        case "setPage":
            return { ...state, page: action.payload }
        case "setTotal":
            return { ...state, total: action.payload }
        default:
            throw new Error("Unknown action.type " + action.type);
    }
}



export default function Index() {
    const [ , setPageTitle] = useOutletContext();
    const [state, dispatch] = useReducer(reducer, initialState);
    const location = useLocation();
    const [searchParams, setSearchParams] = useSearchParams();

    const adminGetRoles = useAdminGetRoles();
    const adminDeleteRole = useAdminDeleteRole();

    const { 
        data: rolesDefinitions, 
        error: getRolesDefinitionsError,
        isLoading: isLoadingRolesDefinitions,
        isValidating: isValidatingRolesDefinitions,
        } = useAdminGetRolesDefinitionsSWR();

    const { 
        data: rights,
        error: getRightsError,
    } = useAdminGetRightsSWR();

    const {data: skills } = useAdminGetSkillsSWR()
    const {data: competitions } = useAdminGetCompetitionsSWR()


    /* On met à jour le titre */
    useEffect(() => {
        setPageTitle("Gestion des rôles");

        return () => {
            setPageTitle("Titre")
        }
    }, []);



    /* On récupère les filtres de recherche si ils sont présents dans l'URL */
    useEffect(() => {
        if (searchParams) {
            dispatch({ type: ACTIONS.SET_FILTERS, payload: queryStringToObject(searchParams.toString()) });
        }
    }, [searchParams])


    /* On met à jour l'URL quand les filtres changent */
    useEffect(() => {
        if(JSON.stringify(state.filters) !== JSON.stringify(searchParams)) {
            setSearchParams(objectToQueryString(state.filters));
        }
    }, [state.filters])



    /* Lorsque la page charge, si les parametres sont vides, on set skip et limit */
      useEffect(() => {
        if (searchParams.size == 0) {
          setSearchParams("limit=10&skip=0")
        }
      }, [])
    
    
      
    /* Lorsque l'on modifie la taille de la page on rafraichit */
    useEffect(() => {
        if(state?.page_size)
            dispatch({ type: ACTIONS.SET_PAGE, payload: 1 })
            dispatch({ type: ACTIONS.SET_FILTER, subtype: "skip", payload: String((state.page - 1) * state.page_size) })
            dispatch({ type: ACTIONS.SET_FILTER, subtype: "limit", payload: String(state.page_size) })
    }, [state.page_size])



    /* Lorsque l'on modifie la page on rafraichit */
    useEffect(() => {
        if(state?.page)
            dispatch({ type: ACTIONS.SET_FILTER, subtype: "skip", payload: String((state.page - 1) * state.page_size) })
    }, [state.page])



    /* Lorsque les états skip et limit ont changé, on rafraichit */
    useEffect(() => {
        if (state.total > 0)
            searchHandler()
    }, [state.filters.skip, state.filters.limit])



    /* On affiche une erreur si les définitions des rôles ne sont pas récupérables */
    useEffect(() => {
        if (getRolesDefinitionsError || getRightsError) {
            console.error("admin/roles/index::error", getRolesDefinitionsError);
            if (isAxiosError(getRolesDefinitionsError)) {
                dispatch({
                    type: ACTIONS.SET_ERROR_MESSAGE,
                    payload: "Impossible de récupérer les définitions des rôles : " + axiosErrorToStringError(getRolesDefinitionsError),
                });
            } else {
                dispatch({
                    type: ACTIONS.SET_ERROR_MESSAGE,
                    payload: "Impossible de récupérer les définitions des rôles : " + getRolesDefinitionsError?.message || "Erreur inconnue",
                });
            }
        } else {
            dispatch({ type: ACTIONS.SET_ERROR_MESSAGE, payload: null });
        }
    }, [getRolesDefinitionsError, getRightsError])



    /* Handler de la recherche */
    const searchHandler = async () => {
        const filters = state.filters;
        dispatch({ type: ACTIONS.SET_IS_LOADING, payload: true });
        dispatch({ type: ACTIONS.SET_ERROR_MESSAGE, payload: null });

        try {
            const [roles, total] = await adminGetRoles(filters).withTotal();
            dispatch({ type: ACTIONS.SET_ROLES, payload: roles });
            dispatch({ type: ACTIONS.SET_IS_LOADING, payload: false });
            dispatch({ type: ACTIONS.SET_TOTAL, payload: total });
        } catch (error) {
            console.error("admin/roles/index::searchHandler", error);
            if (isAxiosError(error)) {
                dispatch({ type: ACTIONS.SET_ERROR_MESSAGE, payload: axiosErrorToStringError(error) });
            } else {
                dispatch({ type: ACTIONS.SET_ERROR_MESSAGE, payload: error?.message || "Erreur inconnue" });
            }
        } finally {
            dispatch({ type: ACTIONS.SET_IS_LOADING, payload: false });
        }
    };



    /* Handler de la suppression d'un rôle */
    const deleteRoleHandler = async (role) => {
        dispatch({ type: ACTIONS.SET_IS_LOADING, payload: true });
        dispatch({ type: ACTIONS.SET_ERROR_MESSAGE, payload: null });

        try {
            await adminDeleteRole(role.id);
            searchHandler();
        } catch (error) {
            console.error("admin/roles/index::deleteRoleHandler::error", error)
            if (isAxiosError(error)) {
                dispatch({ type: ACTIONS.SET_ERROR_MESSAGE, payload: axiosErrorToStringError(error) });
            } else {
                dispatch({ type: ACTIONS.SET_ERROR_MESSAGE, payload: error?.message || "Erreur inconnue" });
            }
        } finally {
            dispatch({ type: ACTIONS.SET_IS_LOADING, payload: false });
        }
    };



    /* Gestion du nettoyage de la recherche */
    const clearFiltersHandler = () => {
        dispatch({ type: ACTIONS.SET_FILTERS, payload: initialState.filters });
    }


    


    return (
    <>
        {/* Filtres de recherche */}

        <h2 className="text-secondary text-lg flex-none content-center">Filtres de recherche</h2>
        <div className="flex flex-wrap w-full justify-start gap-4">
            <div className="flex flex-col justify-between h-12">
                <label className="text-primary text-sm" htmlFor="searchID">
                    ID
                </label>
                <input
                    type="text"
                    id="searchID" name="searchID"
                    className="border border-gray-300 rounded-sm px-2 py-0 text-sm"
                    placeholder="ID"
                    onChange={(e) => dispatch({ type: ACTIONS.SET_FILTER, subtype: "id", payload: e.target.value })}
                    value={state.filters.id}
                />
            </div>
            <div className="flex flex-col justify-between h-12">
                <label className="text-primary text-sm" htmlFor="searchName">
                    Nom
                </label>
                <input
                    type="text"
                    id="searchName" name="searchName"
                    className="border border-gray-300 rounded-sm px-2 py-0 text-sm"
                    placeholder="Nom"
                    onChange={(e) => dispatch({ type: ACTIONS.SET_FILTER, subtype: "name", payload: e.target.value })}
                    value={state.filters.name}
                />
            </div>
            <div className="flex flex-col justify-between">
                <label className="text-primary text-sm" htmlFor="searchType">
                    Type
                </label>
                <select className="border border-gray-300 rounded-sm py-0 text-sm"
                    name="searchType" id="searchType"
                    onChange={(e) => dispatch({ type: ACTIONS.SET_FILTER, subtype: "type", payload: e.target.value })}
                    value={state.filters.type}
                >
                {isLoadingRolesDefinitions || isValidatingRolesDefinitions ? (
                    <option value="" className="text-gray-500">
                        Chargement ...
                    </option>
                    ) : (
                    <>
                        <option value="">-</option>
                        {rolesDefinitions &&
                        Object.keys(rolesDefinitions).map((role, i) => {
                            return (
                            <option key={i} value={role}>
                                {role}
                            </option>
                            );
                        })}
                    </>
                    )}
                </select>
            </div>
            <div className="flex flex-col justify-between h-12">
                <label className="text-primary text-sm" htmlFor="searchName">
                    Caché
                </label>
                <select className="border border-gray-300 rounded-sm py-0 text-sm"
                    name="hidden" id="hidden"
                    onChange={(e) => dispatch({ type: ACTIONS.SET_FILTER, subtype: "hidden", payload: e.target.value })}
                    value={state.filters.hidden}
                >
                    <option value="">-</option>
                    <option value="true">Oui</option>
                    <option value="false">Non</option>
                </select>
            </div>
            <div className="flex flex-col justify-between">
                <label className="text-primary text-sm" htmlFor="searchSkill">
                    Métier
                </label>
                <select className="border border-gray-300 rounded-sm py-0 text-sm"
                    name="searchSkill" id="searchSkill"
                    onChange={(e) => dispatch({ type: ACTIONS.SET_FILTER, subtype: "skill_id", payload: e.target.value })}
                    value={state.filters.skill_id}
                >
                    
                    <>
                        <option value="">-</option>
                        {skills?.map((skill, i) => {
                            return (
                            <option key={i} value={skill.id}>
                                {skill.code} - {skill.name}
                            </option>
                            );
                        })}
                    </>

                </select>
            </div>
            <div className="flex flex-col justify-between">
                <label className="text-primary text-sm" htmlFor="searchCompetition">
                    Compétition
                </label>
                <select className="border border-gray-300 rounded-sm py-0 text-sm"
                    name="searchCompetition" id="searchCompetition"
                    onChange={(e) => dispatch({ type: ACTIONS.SET_FILTER, subtype: "competition_id", payload: e.target.value })}
                    value={state.filters.competition_id}
                >
                    
                    <>
                        <option value="">-</option>
                        {competitions?.map((competition, i) => {
                            return (
                            <option key={i} value={competition.id}>
                                {competition.code} - {competition.name}
                            </option>
                            );
                        })}
                    </>

                </select>
            </div>
            <div className="flex flex-col justify-between">
                <label className="text-primary text-sm" htmlFor="searchRight">
                    Droits
                </label>
                <select className="border border-gray-300 rounded-sm py-0 text-sm"
                    name="searchRight" id="searchRight"
                    onChange={(e) => dispatch({ type: ACTIONS.SET_FILTER, subtype: "right", payload: e.target.value })}
                    value={state.filters.right}
                >
                    <option value="">-</option>
                    {rights && rights.map((right, i) => (
                        <option key={i} value={right}>{right}</option>
                    ))}
                </select>
            </div>
            <div className="flex flex-col justify-end">
                <button 
                    id="searchButton" name="searchButton"
                    className="pb-1" onClick={() => {dispatch({ type: ACTIONS.SET_PAGE, payload: 1 }); searchHandler()}}
                    title="Rechercher">
                    <Search size={32} className="text-primary hover:text-blue-600" />
                </button>
            </div>
            <div className="flex flex-col justify-end">
                <button 
                    id="clearFiltersButton" name="clearFiltersButton"
                    className="pb-1" onClick={() => clearFiltersHandler()}
                    title="Nettoyer la recherche">
                    <SearchX size={20} className="text-primary hover:text-red-600" />
                </button>
            </div>
        </div>


        {/* Affichage de la liste des rôles */}

        <div className="flex flex-wrap w-full h-14 justify-between mb-3 ">
            <div className="flex content-center items-center gap-2">
                <h2 className="text-secondary text-lg flex-none content-center">Liste des rôles</h2>
                <select className="border-1 border-gray-400 py-1 text-sm text-secondary h-10"
                    onChange={(e) => dispatch({ type: ACTIONS.SET_PAGE_SIZE, payload: e.target.value })} 
                    value={state.page_size}
                >
                    <option value="10">10</option>
                    <option value="25">25</option>
                    <option value="50">50</option>
                    <option value="100">100</option>
                </select>
            </div>
            <div className="flex flex-wrap content-center">
                {state.errorMessage && (
                    <span className="text-red-700 flex-none content-center mr-4">{state.errorMessage}</span>
                )}
                {state.isLoading ? <ScaleLoader height={20} color={"#696969"} cssOverride={{alignSelf: "center"}} /> : null}
                <Link
                    to="/admin/roles/add"
                    state={{ from: location}}
                    className="ml-4 border rounded-lg h-10 w-10 border-secondary border-dashed hover:bg-blue-100 content-center"
                >
                    <Plus className="mx-auto" />
                </Link>
            </div>
        </div>
        <table className="table-auto w-full border border-separate rounded-xl border-blue-500 border-spacing-0">
            <thead className="bg-blue-200">
                <tr className="h-10">
                    <th className="px-4 py-2 text-left rounded-tl-xl">Nom</th>
                    <th className="px-4 py-2 text-center">Type</th>
                    <th className="px-4 py-2 text-center">Métier</th>
                    <th className="px-4 py-2 text-center">Compétition</th>
                    <th className="px-4 py-2 text-center rounded-tr-xl w-[30%]">Droits</th>
                </tr>
            </thead>
            <tbody>
                {state.roles?.length > 0 
                    ? ( state.roles.map((role, i) => {
                        return (
                            <tr
                                key={role.id}
                                className={`h-12 ${i % 2 === 0 && "bg-blue-100"} hover:bg-sky-200 `}
                            >
                                <th className={`text-left px-1 ${i === state.roles.length - 1 && "rounded-bl-xl"}`}>
                                    <div className="text-secondary text-xs flex items-center gap-2">
                                        <span className="content-center">ID:{role.id}</span>
                                        <button
                                            onDoubleClick={() => deleteRoleHandler(role)}
                                            title="Double-cliquez pour supprimer le rôle"
                                        >
                                            <Trash size={10} className="text-red-500 hover:text-red-700" />
                                        </button>
                                    </div>
                                    <div className={`text-primary text-base flex items-center`}>
                                        <Link to={"/admin/roles/" + role.id} state={{ from: location}}>
                                            <Pencil size={12} className="flex content-center mr-2" />
                                        </Link>

                                        <span className={`flex content-center ${role.hidden ? "italic font-normal" : ""}`}>
                                            {role.name}
                                        </span>
                                    </div>
                                </th>
                                <td className="text-center">
                                    <span>{role.type}</span>
                                </td>
                                <td className="text-center" >
                                    <div className="flex flex-col content-between gap-1">
                                        <span className="text-xs text-secondary">{role.skill?.id ? `ID:${role.skill?.id}` : ""}</span>
                                        <span className="text-xs text-secondary text-wrap">{role.skill?.code} - {role.skill?.name}</span>
                                    </div>
                                </td>
                                <td className="text-center" >
                                    <div className="flex flex-col content-between gap-1">
                                        <span className="text-xs text-secondary">{role.competition?.id ? `ID:${role.competition?.id}` : ""}</span>
                                        <span className="text-xs text-secondary text-wrap">{role.competition?.code} - {role.competition?.name}</span>
                                    </div>
                                </td>
                                <td className={`px-4 py-2 text-center ${i === state.roles.length - 1 && "rounded-br-xl"}`}>
                                    <div className="flex flex-wrap w-full gap-1 justify-center">
                                        {role?.rights?.map((right, i) => {
                                            const borderColor =
                                                right.includes("_admin")
                                                ? "border-red-500"
                                                : "border-blue-500";

                                            return (
                                                <span
                                                    key={i}
                                                    className={`text-nowrap text-xs border rounded-md bg-slate-100 px-2 py-1 ${borderColor}`}
                                                >
                                                    {right}
                                                </span>
                                            );
                                        })}
                                    </div>
                                </td>
                            </tr>
                        );
                    })) : state.roles ? (
                        <tr>
                            <td colSpan={5} className="text-center text-secondary py-3">
                                Aucun rôle trouvé
                            </td>
                        </tr>
                        ) : (
                            <tr>
                                <td colSpan={5} className="text-center text-secondary py-3">
                                    Lancez la recherche pour afficher les rôles
                                </td>
                            </tr>
                        )}
            </tbody>
        </table>
        { state.total > 0 &&
            <div className="w-full flex flex-row gap-5 justify-end mt-4">
                                      
                <div className="flex flex-row gap-2 items-center border-collapse">
                    
                    <button 
                        className="text-base border border-gray-400 px-2 rounded-s-xl hover:bg-gray-200 text-secondary border-collapse disabled:cursor-not-allowed"
                        disabled={state.page <= 1 || state.isLoading}
                        onClick={(e) => {e.preventDefault(); dispatch({ type: ACTIONS.SET_PAGE, payload: state.page - 1 })}}
                    >
                        <ChevronLeft size={24} />
                    </button>
    
                    <span className="text-secondary text-base border border-gray-400 px-2 border-collapse">
                        {state.page} / {Math.ceil(state.total / state.page_size)}
                    </span>
    
                    <button 
                        className="text-base border border-gray-400 px-2 rounded-e-xl hover:bg-gray-200 text-secondary border-collapse disabled:cursor-not-allowed"
                        disabled={state.page >= Math.ceil(state.total / state.page_size) || state.isLoading}
                        onClick={(e) => {e.preventDefault(); dispatch({ type: ACTIONS.SET_PAGE, payload: state.page + 1 })}}
                    >
                        <ChevronRight size={24} />
                    </button>
        
                </div>
            </div>
        }
    </>
    )
}